import { useMemo } from 'react';
import { AccountFilters } from '@/hooks/useAccounts';
import { AccountFilterSidebar, FilterSection } from './AccountFilterSidebar';
import {
  ACCOUNT_TIER_ORDER,
  ACCOUNT_TIER_LABELS,
  ICP_TIER_ORDER,
  REP_CONFIG,
  CLINICAL_PHASE_ORDER,
  CLINICAL_PHASE_LABELS,
  FUNDING_LABELS,
  EMPLOYEE_RANGE_ORDER,
} from '@/config/filters';

interface AccountsFilterPanelProps {
  filters: AccountFilters;
  onFiltersChange: (filters: AccountFilters) => void;
  filterOptions: {
    countries: string[];
    carrModalities: string[];
    modalities: { value: string; count: number }[];
    employeeRanges: string[];
    accountTiers: string[];
    icpTiers: string[];
    territories: string[];
    therapeuticAreas: { value: string; count: number }[];
    fundingStatuses: string[];
    clinicalPhases: string[];
    primaryCategories?: string[];
    subcategories?: string[];
  } | undefined;
  totalCount: number;
}

export function AccountsFilterPanel({
  filters,
  onFiltersChange,
  filterOptions,
  totalCount,
}: AccountsFilterPanelProps) {

  // ── Map flat filters ↔ sidebar's Record<string, string[]> ────────

  const filterValues: Record<string, string[]> = useMemo(() => ({
    accountTiers: filters.accountTiers || [],
    icpTiers: filters.icpTiers || [],
    territories: filters.territories || [],
    carrModalities: filters.carrModalities || [],
    modalities: filters.modalities || [],
    therapeuticAreas: filters.therapeuticAreas || [],
    clinicalPhases: filters.clinicalPhases || [],
    fundingStatuses: filters.fundingStatuses || [],
    employeeRanges: filters.employeeRanges || [],
    countries: filters.countries || [],
  }), [filters]);

  const toggleValues: Record<string, boolean> = useMemo(() => ({
    topAccount: filters.topAccount || false,
  }), [filters]);

  const handleFilterChange = (sectionId: string, values: string[]) => {
    onFiltersChange({
      ...filters,
      [sectionId]: values.length > 0 ? values : undefined,
      offset: 0,
    });
  };

  const handleToggleChange = (key: string, value: boolean) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
      offset: 0,
    });
  };

  const clearAllFilters = () => {
    onFiltersChange({ limit: filters.limit, offset: 0 });
  };

  const hasActiveFilters = !!(
    filters.searchTerm ||
    filters.territories?.length ||
    filters.countries?.length ||
    filters.carrModalities?.length ||
    filters.modalities?.length ||
    filters.therapeuticAreas?.length ||
    filters.clinicalPhases?.length ||
    filters.fundingStatuses?.length ||
    filters.employeeRanges?.length ||
    filters.topAccount ||
    filters.accountTiers?.length ||
    filters.icpTiers?.length ||
    filters.hideInactive === false
  );

  // ── Build filter sections ─────────────────────────────────────────

  const sections: FilterSection[] = useMemo(() => {
    const sortedAccountTiers = ACCOUNT_TIER_ORDER;
    const sortedIcpTiers = (filterOptions?.icpTiers || []).sort(
      (a, b) => ICP_TIER_ORDER.indexOf(a) - ICP_TIER_ORDER.indexOf(b)
    );
    const sortedEmployeeRanges = (filterOptions?.employeeRanges || []).sort(
      (a, b) => EMPLOYEE_RANGE_ORDER.indexOf(a) - EMPLOYEE_RANGE_ORDER.indexOf(b)
    );
    const sortedClinicalPhases = CLINICAL_PHASE_ORDER.filter(
      (p) => (filterOptions?.clinicalPhases || []).includes(p)
    );

    const s: FilterSection[] = [
      // Account Status
      {
        id: 'accountTiers',
        label: 'Account Status',
        options: sortedAccountTiers.map((t) => ({
          value: t,
          label: ACCOUNT_TIER_LABELS[t] || t,
        })),
      },
      // ICP Tier
      {
        id: 'icpTiers',
        label: 'ICP Tier',
        options: sortedIcpTiers.map((t) => ({ value: t, label: t })),
      },
      // Sales Rep
      {
        id: 'salesRep',
        label: 'Sales Rep',
        type: 'rep' as const,
        options: [],
        reps: REP_CONFIG,
      },
      // Modality Type
      ...(filterOptions?.carrModalities?.length ? [{
        id: 'carrModalities',
        label: 'Modality Type',
        options: filterOptions.carrModalities.map((m) => ({ value: m, label: m })),
      }] : []),
      // Cell Type (filters canonical modality values in companies.modalities[])
      {
        id: 'modalities',
        label: 'Cell Type',
        type: 'badge' as const,
        options: (filterOptions?.modalities || []).map((m) => ({
          value: m.value,
          label: m.value,
          count: m.count,
        })),
      },
      // Therapeutic Area
      {
        id: 'therapeuticAreas',
        label: 'Therapeutic Area',
        type: 'badge' as const,
        options: (filterOptions?.therapeuticAreas || []).map((a) => ({
          value: a.value,
          label: a.value,
          count: a.count,
        })),
      },
      // Clinical Phase
      ...(sortedClinicalPhases.length ? [{
        id: 'clinicalPhases',
        label: 'Clinical Phase',
        type: 'badge' as const,
        options: sortedClinicalPhases.map((p) => ({
          value: p,
          label: CLINICAL_PHASE_LABELS[p] || p,
        })),
      }] : []),
      // Funding Status
      ...(filterOptions?.fundingStatuses?.length ? [{
        id: 'fundingStatuses',
        label: 'Funding Status',
        type: 'badge' as const,
        options: filterOptions.fundingStatuses.map((f) => ({
          value: f,
          label: FUNDING_LABELS[f] || f,
        })),
      }] : []),
      // Company Size
      {
        id: 'employeeRanges',
        label: 'Company Size',
        options: sortedEmployeeRanges.map((r) => ({ value: r, label: r })),
      },
      // Country
      ...(filterOptions?.countries?.length ? [{
        id: 'countries',
        label: 'Country',
        options: filterOptions.countries.map((c) => ({ value: c, label: c })),
      }] : []),
      // Top Accounts
      {
        id: 'topAccount',
        label: 'Top Accounts',
        type: 'toggle' as const,
        options: [],
        toggleKey: 'topAccount',
        toggleLabel: '⭐ Top Accounts Only',
      },
    ];

    return s;
  }, [filterOptions]);

  return (
    <AccountFilterSidebar
      totalCount={totalCount}
      countLabel="accounts"
      searchPlaceholder="Search company name..."
      searchValue={filters.searchTerm || ''}
      onSearchChange={(val) =>
        onFiltersChange({ ...filters, searchTerm: val || undefined, offset: 0 })
      }
      filterSections={sections}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      toggleValues={toggleValues}
      onToggleChange={handleToggleChange}
      onClearFilters={clearAllFilters}
      hasActiveFilters={hasActiveFilters}
      defaultOpenSections={['accountTiers', 'icpTiers']}
      variant="card"
    />
  );
}